import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { UserDeviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-device/user-device-api';
import { RegisterDeviceRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/user/register-device.request';
import { DevicePlatform } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/device-platform.enum';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { NativeBridgeService } from './native-bridge.service';
import { WebViewAction } from '../../enums/webview-action';

interface PendingToken {
    token: string;
    platform: DevicePlatform;
}

@Injectable({
    providedIn: 'root'
})
export class DeviceTokenService implements OnDestroy {
    private userDeviceApi: UserDeviceApi;
    private authApi: AuthApi;
    private currentToken: string | null = null;
    private pendingToken: PendingToken | null = null;
    /** Enregistrement du jeton en attente en cours : évite un double appel quand plusieurs navigations se suivent. */
    private pendingRegistration: Promise<void> | null = null;
    private isRegisteredSubject = new BehaviorSubject<boolean>(false);
    public isRegistered$: Observable<boolean> = this.isRegisteredSubject.asObservable();

    private readonly bridge = inject(NativeBridgeService);
    /** Retire les rappels installés pour la coque. */
    private stopListening: (() => void)[] = [];

    constructor() {
        this.userDeviceApi = new UserDeviceApi();
        this.authApi = new AuthApi();
        this.initializeListeners();
    }

    private initializeListeners(): void {
        this.stopListening = [
            // Called by Flutter when FCM token is available
            this.bridge.listen('onFcmToken', (token: string, platform: string) => {
                console.log('FCM token received from mobile:', token.substring(0, 20) + '...');
                this.handleTokenReceived(token, platform as DevicePlatform);
            }),
            // Called by Flutter when FCM token is refreshed
            this.bridge.listen('onFcmTokenRefresh', (token: string, platform: string) => {
                console.log('FCM token refreshed:', token.substring(0, 20) + '...');
                this.handleTokenReceived(token, platform as DevicePlatform);
            }),
            // Called by Flutter when notification is tapped
            this.bridge.listen('onNotificationTap', (data: any) => {
                if (data && data.deepLink) {
                    window.location.href = data.deepLink;
                }
            }),
        ];
    }

    private async handleTokenReceived(token: string, platform: DevicePlatform): Promise<void> {
        const isAuthenticated = await firstValueFrom(this.authApi.isAuthenticate());

        if (isAuthenticated && (await this.registerToken(token, platform))) {
            return;
        }
        // Store for later registration (after login, or on the next navigation if the API failed)
        this.pendingToken = { token, platform };
        console.log('Token stored for later registration');
    }

    /** @returns true si l'API a enregistré l'appareil. */
    async registerToken(token: string, platform: DevicePlatform): Promise<boolean> {
        try {
            const request: RegisterDeviceRequest = {
                fcmToken: token,
                platform,
                deviceName: this.getDeviceName(platform),
            };

            await this.userDeviceApi.register(request);
            this.currentToken = token;
            this.isRegisteredSubject.next(true);
            console.log('Device registered successfully');
            return true;
        } catch (error) {
            console.error('Failed to register device:', error);
            return false;
        }
    }

    /**
     * Enregistre le jeton reçu avant la session, dès qu'une session existe.
     *
     * Appelé après chaque navigation : sans session, ou si l'API échoue, le
     * jeton reste en attente pour la tentative suivante. L'oublier sur un
     * échec, c'était un nouvel installé sans push jusqu'au lancement suivant.
     */
    registerPendingToken(): Promise<void> {
        if (!this.pendingToken) {
            return Promise.resolve();
        }
        if (!this.pendingRegistration) {
            this.pendingRegistration = this.tryRegisterPendingToken().finally(() => {
                this.pendingRegistration = null;
            });
        }
        return this.pendingRegistration;
    }

    private async tryRegisterPendingToken(): Promise<void> {
        const pending = this.pendingToken;
        if (!pending || !(await firstValueFrom(this.authApi.isAuthenticate()))) {
            return;
        }
        if (await this.registerToken(pending.token, pending.platform)) {
            // Un jeton plus récent a pu arriver entre-temps : ne l'effacer que s'il est inchangé.
            if (this.pendingToken === pending) {
                this.pendingToken = null;
            }
        }
    }

    async unregisterCurrentDevice(): Promise<void> {
        if (this.currentToken) {
            try {
                await this.userDeviceApi.unregister({ fcmToken: this.currentToken });
                this.currentToken = null;
                this.isRegisteredSubject.next(false);
                console.log('Device unregistered successfully');
            } catch (error) {
                console.error('Failed to unregister device:', error);
            }
        }
    }

    requestTokenFromMobile(): void {
        // Request FCM token from Flutter via JavaScript channel
        this.bridge.post(WebViewAction.fcmToken, 'request');
    }

    private getDeviceName(platform: DevicePlatform): string {
        if (platform === DevicePlatform.IOS) {
            return 'iPhone';
        } else if (platform === DevicePlatform.ANDROID) {
            return 'Android Device';
        }
        return 'Web Browser';
    }

    get hasPendingToken(): boolean {
        return this.pendingToken !== null;
    }

    ngOnDestroy(): void {
        // Clean up the global functions
        this.stopListening.forEach((stop) => stop());
        this.stopListening = [];
    }
}
